/**
 * Configuration globale axios — B-12 Phase 3.
 *
 * Active `withCredentials: true` sur TOUTES les requêtes axios de l'app.
 * Cela permet d'envoyer automatiquement le cookie HttpOnly `access_token`
 * (émis par /api/auth/login depuis B-12 Phase 1) sur les appels API.
 *
 * Pour l'instant, le Bearer header reste utilisé en parallèle (transition
 * douce, pas d'invalidation des sessions actives). Le backend accepte les
 * 2 via `cookie_auth.get_current_user_hybrid` (B-12 Phase 2).
 *
 * Quand toutes les routes auront été migrées vers la dépendance hybride,
 * on pourra :
 *  1. Supprimer le stockage du token dans `localStorage` (tokenService.js)
 *  2. Supprimer l'envoi du header `Authorization: Bearer …`
 *  3. Déprécier le Bearer côté backend (B-12 Phase 5)
 *
 * USAGE : importer dans `App.js` AVANT toute autre import qui utilise axios.
 *
 *   import './config/axiosConfig';
 *
 * Note : le SSL strict du proxy K8s exige que `withCredentials=true` soit
 * couplé à `Access-Control-Allow-Credentials: true` côté serveur — déjà
 * configuré dans `backend/app_cors.py:allow_credentials=True`.
 */

import axios from 'axios';

// Envoyer les cookies HttpOnly sur TOUTES les requêtes (même cross-origin)
axios.defaults.withCredentials = true;

// Header par défaut pour les requêtes JSON (déjà standard, on l'affirme)
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// B-12 Phase 4 — Intercepteur CSRF (double-submit cookie)
// Lit le cookie `csrf_token` (non HttpOnly) et le renvoie dans le header
// `X-CSRF-Token` sur toutes les mutations (POST/PUT/PATCH/DELETE).
function _readCookie(name) {
  const all = document.cookie ? document.cookie.split(';') : [];
  for (const c of all) {
    const trimmed = c.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return '';
}

axios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  // Espace administration : permet au serveur d'appliquer le rôle personnalisé du compte.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
    config.headers = config.headers || {};
    config.headers['X-Admin-Context'] = '1';
  }
  // Méthodes safe (GET/HEAD/OPTIONS) ne nécessitent pas de CSRF
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrf = _readCookie('csrf_token');
    if (csrf) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

export default axios;
